import { motion } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { lineLabels, type Product } from '@/data/products'
import { cn } from '@/lib/utils'

interface ProductCardProps {
  product: Product
  index?: number
  className?: string
  /** Mostra il numero editoriale 01, 02… in alto a sinistra. Default true. */
  showNumeral?: boolean
}

export function ProductCard({
  product,
  index = 0,
  className,
  showNumeral = true,
}: ProductCardProps) {
  const numeral = String(index + 1).padStart(2, '0')

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.05 }}
      className={cn('group relative', className)}
    >
      <LocalizedLink
        to={`/prodotti/${product.slug}`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-argan focus-visible:ring-offset-4 focus-visible:ring-offset-cream"
      >
        {/* Image stage — il fondo del PNG (#FFFAF4) funge da riquadro naturale */}
        <div className="relative overflow-hidden aspect-[4/5] bg-cream">
          {showNumeral && (
            <span
              aria-hidden="true"
              className="nashi-numeral absolute top-4 left-5 z-10 text-bruno-muted/80 transition-colors duration-500 group-hover:text-argan-deep"
            >
              {numeral}
            </span>
          )}

          {/* Subtle ambient gradient that intensifies on hover */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_60%,rgba(207,169,128,0.12),transparent_60%)] opacity-0 transition-opacity duration-700 group-hover:opacity-100"
          />

          {product.hoverImage ? (
            <>
              <img
                src={product.image}
                alt={product.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-contain transition-opacity duration-500 group-hover:opacity-0"
              />
              <img
                src={product.hoverImage}
                alt={product.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              />
            </>
          ) : (
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}

          {/* Reveal CTA on hover */}
          <span
            aria-hidden="true"
            className="absolute bottom-4 right-4 z-10 inline-flex h-10 w-10 translate-y-2 items-center justify-center rounded-full border border-argan/40 bg-cream/80 text-argan-deep opacity-0 backdrop-blur transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100"
          >
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </span>
        </div>

        {/* Meta */}
        <div className="mt-5 flex items-baseline justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
              {lineLabels[product.line]}
            </p>
            <h3 className="mt-2 font-display italic text-bruno text-xl md:text-2xl leading-tight transition-colors duration-500 group-hover:text-argan-deep">
              {product.name}
            </h3>
          </div>
          {product.format && (
            <span className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-bruno-soft tabular-nums">
              {product.format}
            </span>
          )}
        </div>

        {/* Underline that grows on hover */}
        <span className="mt-3 block h-px w-0 bg-argan transition-all duration-700 ease-out group-hover:w-full" />
      </LocalizedLink>
    </motion.div>
  )
}
